"use client";

import { useState, useRef, useEffect } from "react";
import { Mic, MicOff, Play, Pause, Save, X, RotateCcw } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Progress } from "@/app/_module/components/ui/progress";
import { Input } from "@/app/_module/components/ui/input";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useVoiceDeviceContext } from "@/app/_module/contexts/VoiceDeviceProvider";

interface VoiceRecorderProps {
  onSave: (audioBlob: Blob, name: string) => Promise<void>;
  onClose: () => void;
}

export function VoiceRecorder({ onSave, onClose }: VoiceRecorderProps) {
  const { toast } = useToast();
  const { selectedInputDeviceId } = useVoiceDeviceContext();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [voicemailName, setVoicemailName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl]);

  const convertToWav = async (webmBlob: Blob): Promise<Blob> => {
    const arrayBuffer = await webmBlob.arrayBuffer();
    const audioContext = new AudioContext();
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const sourceData = audioBuffer.getChannelData(0);

    const targetSampleRate = 16000;
    const ratio = audioBuffer.sampleRate / targetSampleRate;
    const targetLength = Math.floor(sourceData.length / ratio);
    const channelData = new Float32Array(targetLength);

    for (let i = 0; i < targetLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, sourceData.length - 1);
      const frac = srcIndex - srcIndexFloor;
      channelData[i] = sourceData[srcIndexFloor] * (1 - frac) + sourceData[srcIndexCeil] * frac;
    }

    const wavLength = 44 + channelData.length * 2;
    const wav = new ArrayBuffer(wavLength);
    const view = new DataView(wav);

    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeString(0, 'RIFF');
    view.setUint32(4, wavLength - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, targetSampleRate, true);
    view.setUint32(28, targetSampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);

    writeString(36, 'data');
    view.setUint32(40, channelData.length * 2, true);

    let offset = 44;
    for (let i = 0; i < channelData.length; i++) {
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
      offset += 2;
    }

    await audioContext.close();
    return new Blob([wav], { type: 'audio/wav' });
  };

  const startRecording = async () => {
    try {
      let micDeviceId = selectedInputDeviceId;
      if (!micDeviceId) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const realMics = devices.filter(d =>
          d.kind === 'audioinput' &&
          !d.label.toLowerCase().includes('virtual') &&
          !d.label.toLowerCase().includes('zoom') &&
          !d.label.toLowerCase().includes('blackhole') &&
          d.deviceId !== 'default' &&
          d.deviceId !== 'communications'
        );
        if (realMics.length > 0) {
          micDeviceId = realMics[0].deviceId;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: micDeviceId ? { exact: micDeviceId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000,
      });

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const webmBlob = new Blob(audioChunksRef.current, { type: mimeType });

        try {
          const wavBlob = await convertToWav(webmBlob);
          setAudioBlob(wavBlob);
          const url = URL.createObjectURL(wavBlob);
          setAudioUrl(url);
        } catch {
          setAudioBlob(webmBlob);
          const url = URL.createObjectURL(webmBlob);
          setAudioUrl(url);
        }

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setIsRecording(true);
      setIsPaused(false);

      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 120) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      toast({
        title: "Microphone Access Denied",
        description: "Please allow microphone access to record voicemail drops.",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);

        timerIntervalRef.current = setInterval(() => {
          setRecordingTime(prev => {
            if (prev >= 120) {
              stopRecording();
              return prev;
            }
            return prev + 1;
          });
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);

        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      }
    }
  };

  const playRecording = () => {
    if (audioUrl) {
      if (!audioRef.current) {
        audioRef.current = new Audio(audioUrl);
        audioRef.current.addEventListener('ended', () => {
          setIsPlaying(false);
        });
      }

      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
    }
  };

  const resetRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
    setVoicemailName("");
    audioChunksRef.current = [];
  };

  const handleSave = async () => {
    if (!audioBlob || !voicemailName.trim()) return;

    setIsSaving(true);

    try {
      await onSave(audioBlob, voicemailName.trim());

      toast({
        title: "Voicemail Drop Saved",
        description: "Your recorded voicemail drop has been saved successfully.",
      });

      resetRecording();
      onClose();
    } catch (error) {
      toast({
        title: "Save Failed",
        description: "Failed to save voicemail drop. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    resetRecording();
    onClose();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressPercent = (recordingTime / 120) * 100;

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 animate-in slide-in-from-top-2 fade-in duration-300">
      {/* Close button */}
      <button
        onClick={handleClose}
        disabled={isSaving}
        className="absolute top-3 right-3 z-10 flex items-center justify-center h-7 w-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-all"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 shadow-sm">
            <Mic className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-slate-700">Record Voicemail</span>
        </div>

        {/* Timer + Progress */}
        <div className="flex items-center gap-4 mb-5">
          <div className="text-3xl font-mono font-bold text-slate-800 tabular-nums tracking-tight">
            {formatTime(recordingTime)}
          </div>
          <div className="flex-1">
            <Progress value={progressPercent} className="h-1.5" />
            <p className="text-[10px] text-slate-400 mt-1 text-right">
              {isRecording && !isPaused && "Recording..."}
              {isPaused && "Paused"}
              {!isRecording && audioBlob && "Ready to save"}
              {!isRecording && !audioBlob && "2:00 max"}
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          {/* Main action button */}
          {!isRecording && !audioBlob && (
            <button
              onClick={startRecording}
              className="flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg shadow-red-200 hover:shadow-red-300 hover:scale-105 active:scale-95 transition-all duration-200"
            >
              <Mic className="h-6 w-6" />
            </button>
          )}

          {isRecording && (
            <>
              <button
                onClick={pauseRecording}
                className="flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:scale-105 active:scale-95 transition-all"
              >
                {isPaused ? <Play className="h-4 w-4 ml-0.5" /> : <Pause className="h-4 w-4" />}
              </button>
              <button
                onClick={stopRecording}
                className="relative flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg shadow-red-200 hover:scale-105 active:scale-95 transition-all duration-200"
              >
                <MicOff className="h-6 w-6" />
                {!isPaused && (
                  <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-20" />
                )}
              </button>
            </>
          )}

          {!isRecording && audioBlob && (
            <>
              <button
                onClick={playRecording}
                className={`flex items-center justify-center h-11 w-11 rounded-full transition-all duration-200 hover:scale-105 active:scale-95 ${
                  isPlaying
                    ? "bg-gradient-to-br from-indigo-500 to-blue-600 text-white shadow-lg shadow-indigo-200"
                    : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
              </button>
              <button
                onClick={resetRecording}
                className="flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-400 hover:text-slate-600 hover:bg-slate-50 hover:scale-105 active:scale-95 transition-all"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Spacer + Inline name input & save when recording is done */}
          {!isRecording && audioBlob && (
            <div className="flex-1 flex items-center gap-2">
              <Input
                placeholder="Name this drop..."
                value={voicemailName}
                onChange={(e) => setVoicemailName(e.target.value)}
                maxLength={100}
                className="h-10 text-sm bg-white border-slate-200 focus-visible:ring-indigo-300"
              />
              <Button
                onClick={handleSave}
                disabled={isSaving || !voicemailName.trim()}
                size="sm"
                className="h-10 px-4 bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white shadow-sm shrink-0"
              >
                <Save className="h-3.5 w-3.5 mr-1.5" />
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
